import type { DiscoveredRow } from '../../api/inventoree'
import { escapeHtml, fromNow, stamp } from '../format'

/**
 * DiscoveryReadiness + DiscoveryPrecheck din public/js/discovery.js (cec-master,
 * 26.9.10.13 si "Check before deploy").
 *
 * Serverul trimite un COD in `deploy_ready`, socotit din porturile deschise la
 * ultima scanare (DeployReadiness in API). Aici se scrie doar ce citeste omul:
 * textul spune ce e DE FACUT, nu ce lipseste. Ce se poate instala e verde, ce
 * se rezolva cu o scanare e galben, ce n-are cum se rezolva e rosu.
 *
 * `precheck_result` e a doua pastila din aceeasi celula: DOVADA de pe releu, cu
 * chiar contul din seif, nu deductia din scanare. Nu se topesc intr-una - o
 * deductie verde langa o dovada rosie e chiar informatia utila.
 */

interface State {
  text: string
  css: string
  why: string
}

const READY: Record<string, State> = {
  windows: { text: 'Ready (Windows)', css: 'label-success', why: 'Port 135 is open: the agent goes on through a scheduled task.' },
  ssh: { text: 'Ready (SSH)', css: 'label-success', why: 'Port 22 is open: the agent goes on over SSH.' },
  unknown: {
    text: 'No services scan yet',
    css: 'label-warning',
    why: 'Nothing is known about the open ports. Run a services scan on this range and this row will answer.',
  },
  no_relay: {
    text: 'No relay in this range',
    css: 'label-info',
    why: 'An install starts from a relay on the same network, and this device was not found by one.',
  },
  closed: {
    text: 'No way in',
    css: 'label-danger',
    why: 'The scan found the open ports, and neither 135 (Windows) nor 22 (SSH) is among them.',
  },
  managed: { text: 'Already managed', css: 'label-primary', why: 'This machine already has an agent - the relay itself counts.' },
  not_a_target: { text: 'Not a target', css: 'label-default', why: 'An agent does not go on a printer, a switch or a camera.' },
}

const UNKNOWN_STATE: State = {
  text: 'Not known',
  css: 'label-default',
  why: 'The list did not say whether the agent can go on this machine.',
}

const PRECHECK: Record<string, State> = {
  pending: { text: 'Checking...', css: 'label-warning', why: 'The relay was asked to try. The answer lands here when it reports back.' },
  ok: { text: 'Proven', css: 'label-success', why: 'The relay reached this machine and the credential was accepted. An install would start.' },
  auth: {
    text: 'Credential refused',
    css: 'label-danger',
    why: 'The machine answered, and it rejected this account. Fix the Pass Safe entry, not the network.',
  },
  no_route: { text: 'No way in', css: 'label-danger', why: 'Neither 135 (Windows) nor 22 (SSH) answered when the relay tried, right now.' },
  relay: {
    text: 'Wrong relay',
    css: 'label-info',
    why: 'This relay cannot drive this machine - a Windows target needs a Windows relay, or the GPO/Intune package.',
  },
  blocked: { text: 'Out of range', css: 'label-default', why: 'The address on this row is outside the private ranges, and a deploy would refuse it too.' },
  no_credential: {
    text: 'Credential not released',
    css: 'label-danger',
    why: 'The Pass Safe entry could not be handed to the relay. Nothing was tried on the machine.',
  },
  error: { text: 'Check failed', css: 'label-danger', why: 'The relay could not finish the check. The reason is on the row.' },
}

function readyState(code: unknown): State {
  return READY[String(code ?? '')] ?? UNKNOWN_STATE
}

/** Pastila din coloana "Deploy ready". */
export function readinessBadge(row: Pick<DiscoveredRow, 'deploy_ready'>): string {
  const one = readyState(row.deploy_ready)
  /* "From the last scan" e spus de la inceput: in aceeasi celula sta si pastila
     probei, care e o dovada de acum. */
  return `<label class="label ${one.css}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(`From the last scan: ${one.why}`)}">${escapeHtml(one.text)}</label>`
}

/** Doar cuvintele, pentru desfacerea randului. */
export function readinessWords(code: unknown): string {
  const one = readyState(code)
  return `${one.text} - ${one.why}`
}

/** Un rand fara verdict nu deseneaza nimic. */
export function precheckBadge(row: Pick<DiscoveredRow, 'precheck_result' | 'precheck_problem' | 'precheck_at'>, now: Date = new Date()): string {
  const one = PRECHECK[String(row.precheck_result ?? '')]
  if (!one) return ''
  const reason = String(row.precheck_problem ?? '').trim()
  const why = reason === '' ? one.why : `${one.why} - ${reason}`
  const at = stamp(row.precheck_at ?? null)
  const when = at ? ` (${fromNow(at, now)})` : ''
  return `<div style="margin-top:3px"><label class="label ${one.css}" data-toggle="tooltip" data-placement="top" title="${escapeHtml(`Live check${when}: ${why}`)}"><i class="fas fa-vial" aria-hidden="true"></i> ${escapeHtml(one.text)}</label></div>`
}

export function precheckWords(code: unknown): string {
  const one = PRECHECK[String(code ?? '')]
  return one ? `${one.text} - ${one.why}` : ''
}

export function precheckVia(code: unknown): string {
  return String(code) === 'windows' ? 'Windows Task Scheduler (135)' : 'SSH (22)'
}
